import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import PageHeader from "../../Component/PageHeader";
import StatStrip from "../../Component/StatStrip";
import PeriodFilter, { periodRange } from "../../Component/PeriodFilter";
import Pill, { PillTone } from "../../Component/Pill";
import Loader from "../../Component/Loader/Loader";
import WabaDetailsModal from "../Users/modals/WabaDetailsModal";
import { getRequest } from "../../utils/api";
import { logout } from "../../utils/auth";

// One synced phone number from Meta (user_meta_account_info.phone_numbers.data[])
interface WaNumber {
  id: string;
  display_phone_number?: string;
  status?: string;
  name_status?: string;
  code_verification_status?: string;
  quality_rating?: string;
}

// Row returned by GET /user/whatsappNumbers (workspaces with isWabaConnected = true)
interface WaRow {
  _id: string;
  user_unique_id?: string;
  user_first_name?: string;
  user_last_name?: string;
  user_email?: string;
  user_companyName?: string;
  user_plan_name?: string;
  user_status?: number;
  user_whatsapp_type?: string;
  user_whatsapp?: { wabaId?: string; phoneNumberId?: string; businessId?: string; isWabaConnected?: boolean; error_message?: string };
  user_meta_account_info?: any;
  user_meta_analytics?: any;
  user_dash_lastFetchedAt?: string;
  wa_number?: any;
  wa_numbers_count: number;
}

// Interface for Stats (value + growth % vs previous period, same as /userStats)
interface StatItem {
  value: number;
  growth: number;
}

interface WaStats {
  total: StatItem;
  green: StatItem;
  yellow: StatItem;
  red: StatItem;
  unknown: StatItem;
}

const EMPTY_STAT: StatItem = { value: 0, growth: 0 };

const QUALITY: Record<string, [PillTone, string, string]> = {
  GREEN: ["ok", "Green", "#0B9B50"],
  YELLOW: ["warn", "Yellow", "#B8770B"],
  RED: ["bad", "Red", "#D8412A"],
};
const UNKNOWN_Q: [PillTone, string, string] = ["none", "Unknown", "#c0c4cc"];
const STATUS_TONE: Record<string, PillTone> = {
  CONNECTED: "ok", APPROVED: "ok", VERIFIED: "ok", AVAILABLE_WITHOUT_REVIEW: "ok",
  PENDING: "warn", PENDING_REVIEW: "warn", NOT_VERIFIED: "warn",
  DISCONNECTED: "bad", FLAGGED: "bad", RESTRICTED: "bad", BANNED: "bad", DECLINED: "bad", REJECTED: "bad",
};
// Dot colour for each STATUS_TONE (number status dot next to the phone number)
const TONE_COLOR: Record<PillTone, string> = {
  ok: "#0B9B50",
  warn: "#B8770B",
  bad: "#D8412A",
  none: "#c0c4cc",
  blue: "#032e67",
};
const CHIPS: [string, string, keyof WaStats][] = [
  ["all", "All", "total"], ["RED", "Red", "red"], ["YELLOW", "Yellow", "yellow"], ["GREEN", "Green", "green"], ["UNKNOWN", "Unknown", "unknown"],
];

const human = (s?: string) => (s ? s.replace(/_/g, " ").toLowerCase() : "");

const WhatsAppNumbers = () => {
  const navigate = useNavigate();
  const [numberList, setNumberList] = useState<WaRow[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalNumbers, setTotalNumbers] = useState<number>(0);
  const [isLoader, setIsLoader] = useState<boolean>(true);

  // Modal
  const [showWabaModal, setShowWabaModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<WaRow | null>(null);

  // Stats State
  const [stats, setStats] = useState<WaStats>({
    total: EMPTY_STAT,
    green: EMPTY_STAT,
    yellow: EMPTY_STAT,
    red: EMPTY_STAT,
    unknown: EMPTY_STAT
  });

  // Filters
  const [period, setPeriod] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [qualityFilter, setQualityFilter] = useState("all");

  // Fetch WhatsApp Number List (list + stats follow the selected period)
  const getNumberList = useCallback(async () => {
    // All time = no dates; custom waits until both dates are picked
    const [from, to] = periodRange(period, customFrom, customTo);
    if (period === "custom" && (!from || !to)) return;
    const startDate = from ? from.format('YYYY-MM-DD') : "";
    const endDate = to ? to.format('YYYY-MM-DD') : "";
    try {
      setIsLoader(true);
      const response = await getRequest(
        `/user/whatsappNumbers?page=${page}&limit=${pageSize}&quality=${qualityFilter}&search=${encodeURIComponent(searchQuery)}&startDate=${startDate}&endDate=${endDate}`
      );

      if (response.status === 'success') {
        setNumberList(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalNumbers(response.pagination.total);
        if (response.stats) {
          setStats(response.stats);
        }
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Something went wrong loading WhatsApp numbers!");
    } finally {
      setIsLoader(false);
    }
  }, [page, pageSize, qualityFilter, searchQuery, period, customFrom, customTo]);

  useEffect(() => {
    getNumberList();
  }, [getNumberList]);

  // Debounced Search
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText);
      setPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  const openWabaDetails = (user: WaRow) => {
    setSelectedUser(user);
    setShowWabaModal(true);
  };

  // Page numbers with gaps (1 … 4 5 6 … 20)
  const pageItems = (): (number | "gap")[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const out: (number | "gap")[] = [1];
    if (page > 3) out.push("gap");
    for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) out.push(p);
    if (page < totalPages - 2) out.push("gap");
    out.push(totalPages);
    return out;
  };

  const firstRow = totalNumbers ? (page - 1) * pageSize + 1 : 0;
  const lastRow = Math.min(page * pageSize, totalNumbers);

  return (
    <div className="container-fluid">
      {isLoader && <Loader />}
      <div className="cx">
        <PageHeader
          crumb="WhatsApp numbers"
          title="WhatsApp numbers"
          sub="Every workspace with a connected WABA — quality rating and status as last synced from Meta."
          actions={
            <PeriodFilter
              allTime
              value={period}
              onChange={(p) => { setPeriod(p); setPage(1); }}
              from={customFrom}
              to={customTo}
              onRangeChange={(f, t) => { setCustomFrom(f); setCustomTo(t); setPage(1); }}
            />
          }
        />

        {/* All time has no previous period, so no growth % */}
        <StatStrip items={[
          { label: "Connected numbers", value: stats.total.value, icon: "ri-whatsapp-line", delta: period === "all" ? "All time" : stats.total.growth },
          { label: "Quality: Green", value: stats.green.value, icon: "ri-shield-check-line", delta: period === "all" ? "All time" : stats.green.growth },
          { label: "Quality: Yellow", value: stats.yellow.value, icon: "ri-alert-line", delta: period === "all" ? "All time" : stats.yellow.growth, invert: true },
          { label: "Quality: Red", value: stats.red.value, icon: "ri-error-warning-line", delta: period === "all" ? "All time" : stats.red.growth, invert: true },
          { label: "Unknown / not synced", value: stats.unknown.value, icon: "ri-question-line", delta: period === "all" ? "All time" : stats.unknown.growth, invert: true },
        ]} />

        <div className="cx-card">
          <div className="cx-toolbar">
            <div className="cx-chips">
              {CHIPS.map(([k, l, sk]) => (
                <button type="button" key={k} className={`cx-chip ${qualityFilter === k ? "on" : ""}`} onClick={() => { setQualityFilter(k); setPage(1); }}>
                  {QUALITY[k] && <span className="cx-qdot" style={{ background: QUALITY[k][2], margin: 0 }}></span>}
                  {l} <b>{stats[sk].value.toLocaleString("en-IN")}</b>
                </button>
              ))}
            </div>
            <div className="sp"></div>
            <input className="cx-input" type="search" placeholder="Search number, WABA or workspace…" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          </div>
          <div className="cx-tbl-wrap">
            <table className="cx-tbl">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Workspace</th>
                  <th>Display name</th>
                  <th>Quality</th>
                  <th>Mode</th>
                  <th>Last synced</th>
                  <th className="r">Actions</th>
                  </tr>
              </thead>
              <tbody>
                {numberList.length ? numberList.map(r => {
                  const n = r.user_meta_analytics;
                  const [qTone, qLabel] = QUALITY[n?.quality_rating || ""] || UNKNOWN_Q;
                  const statusColor = TONE_COLOR[STATUS_TONE[n?.status || ""] || "none"];
                  const statusTitle = n?.status ? `Number status: ${human(n.status)}` : "Number status not synced";
                  const meta = r.user_meta_account_info || {};
                  const waNumber = r.wa_number || {};
                  const owner = `${r.user_first_name || ""} ${r.user_last_name || ""}`.trim();
                  return (
                    <tr key={r._id} className="click" onClick={() => openWabaDetails(r)}>
                      <td>
                        {n?.display_phone_number
                          ? <span className="mono"><span className="cx-qdot" style={{ background: statusColor }} title={statusTitle}></span>{n.display_phone_number}</span>
                          : <span className="muted fs12"><span className="cx-qdot" style={{ background: statusColor }} title={statusTitle}></span>Not synced yet</span>}
                        
                      </td>
                      <td>
                        <b style={{ fontWeight: 600, fontSize: 13 }}>{r.user_companyName || owner || r.user_email}</b>
                        <div className="fs12 muted">WABA - {r.user_whatsapp?.wabaId || ""}{r.user_plan_name ? ` · ${r.user_plan_name}` : ""}</div>
                      </td>
                      <td>
                        {meta.name || <span className="muted">—</span>}
                        {waNumber?.name_status && <div className="fs12 muted">{human(waNumber.name_status)}</div>}
                      </td>
                      <td><Pill tone={qTone}>{qLabel}</Pill></td>
                      <td className="fs12">{r.user_whatsapp_type === "coexistence" ? "Coexistence" : "Cloud API"}</td>
                      
                      <td className="fs12 muted" title={r.user_dash_lastFetchedAt ? moment(r.user_dash_lastFetchedAt).format("DD MMM YYYY, HH:mm") : ""}>
                        {r.user_dash_lastFetchedAt ? moment(r.user_dash_lastFetchedAt).fromNow() : "Never"}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="cx-ra">
                          <button type="button" className="v" title="WABA details / sync" onClick={() => openWabaDetails(r)}><i className="ri-eye-line"></i></button>
                          <button type="button" title="Open workspace" onClick={() => navigate(`/customers-and-users/view-user/${r._id}`)}><i className="ri-external-link-line"></i></button>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={9}><div className="cx-empty"><i className="ri-whatsapp-line"></i>{isLoader ? "Loading…" : "No numbers match these filters."}</div></td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="cx-tfoot">
            <span>Showing <b>{firstRow}–{lastRow}</b> of <b>{(totalNumbers || 0).toLocaleString("en-IN")}</b></span>
            <span>Filter <b>Red</b> to find numbers that need action — pause broadcasts or warn the client.</span>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span>Rows</span>
              <select className="cx-select" value={pageSize} onChange={(e) => { setPageSize(+e.target.value); setPage(1); }}>
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <div className="cx-pager">
                <button type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
                {pageItems().map((p, i) =>
                  typeof p === "number"
                    ? <button type="button" key={p} className={p === page ? "on" : ""} onClick={() => setPage(p)}>{p}</button>
                    : <span key={`gap-${i}`} className="gap">…</span>
                )}
                <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>›</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* existing modal: shows the synced WABA info and can re-sync / register the number with Meta */}
      <WabaDetailsModal
        show={showWabaModal}
        onHide={() => setShowWabaModal(false)}
        userID={selectedUser?._id || ""}
        initialData={selectedUser?.user_meta_account_info || {}}
        userData={selectedUser || {}}
        onSyncComplete={getNumberList}
      />
    </div>
  );
};

export default WhatsAppNumbers;
